import type { Feature } from '@kb3d/models/classes/features/feature';
import type { SubmeshData } from '@kb3d/models/classes/features/submesh-feature';
import type { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import type { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

export function calculateBaseSubmeshes(
    vertexDataCurrent: SubmeshVertexData,
    vertexDataNew: Partial<SubmeshVertexData>,
    submeshData: SubmeshData[]
) {
    submeshData.sort((a, b) => a.indexStart - b.indexStart);
    const indicesCount = (vertexDataNew.indices || vertexDataCurrent.indices!).length;

    const baseSubmeshes = [
        {
            indexStart: 0,
            indexCount: indicesCount,
        },
    ];

    submeshData.forEach(submesh => {
        const lastSubmesh = baseSubmeshes[baseSubmeshes.length - 1];
        if (lastSubmesh) {
            if (
                submesh.indexStart <= lastSubmesh.indexStart &&
                submesh.indexStart + submesh.indexCount > lastSubmesh.indexStart
            ) {
                lastSubmesh.indexStart = submesh.indexStart + submesh.indexCount;
                lastSubmesh.indexCount = indicesCount - lastSubmesh.indexStart;

                if (lastSubmesh.indexCount <= 0) {
                    baseSubmeshes.splice(-1, 1);
                }
            } else if (submesh.indexStart > lastSubmesh.indexStart) {
                lastSubmesh.indexCount = submesh.indexStart - lastSubmesh.indexStart;

                const remainingIndexCount = indicesCount - (submesh.indexStart + submesh.indexCount);
                if (remainingIndexCount > 0) {
                    baseSubmeshes.push({
                        indexStart: submesh.indexStart + submesh.indexCount,
                        indexCount: remainingIndexCount,
                    });
                }
            }
        }
    });

    return baseSubmeshes;
}

export class FeatureOrderManager {
    protected featureOrder = new Map<string, string[]>();
    getMovedFeatureIndex(mesh: MeshNode) {
        const oldArray = this.featureOrder.get(mesh._dynamicId);
        if (oldArray) {
            if (oldArray.length !== mesh.features.length) {
                return -1;
            }
            for (let i = 0; i < oldArray.length; i++) {
                const newArrayId = mesh.features[i]._dynamicId;
                if (oldArray[i] !== newArrayId) {
                    return i;
                }
            }
        } else {
            return -1;
        }
    }
    
    updateFeatureOrder(mesh: MeshNode) {
        const features: string[] = [];
        mesh.features.forEach(feature => {
            features.push(feature._dynamicId);
        });
        this.featureOrder.set(mesh._dynamicId, features);
    }
    
    recursivelyUpdateFeatureVisibility(
        affectedFeatures: Feature[],
        features: Feature[],
        disabledFeatures: Feature[]
    ) {
        if (affectedFeatures.length === 0) {
            disabledFeatures.forEach(feature => {
                console.warn(
                    'The feature named',
                    feature.name,
                    'got disabled due to a possible breaking change in the feature stack.'
                );
            });
            return;
        }
    
        const newAffectedFeatures: Feature[] = [];
        affectedFeatures.forEach(affectedFeature => {
            const featureIndex = getFeatureIndex(affectedFeature, features);
            if (featureIndex >= 0) {
                for (let i = featureIndex; i < features.length; i++) {
                    const feature = features[i];
                    if (feature.enabled) {
                        affectedFeature.affects?.forEach(affector => {
                            if (feature.dependsOn && feature.dependsOn.has(affector)) {
                                feature.enabled = false;
                                disabledFeatures.push(feature);
                                feature._disableFeatureStackChange = true;
                                newAffectedFeatures.push(feature);
                            }
                        });
                    }
                }
            }
        });
        this.recursivelyUpdateFeatureVisibility(newAffectedFeatures, features, disabledFeatures);
    
        function getFeatureIndex(feature: Feature, features: Feature[]) {
            for (let i = 0; i < features.length; i++) {
                if (features[i] === feature) {
                    return i;
                }
            }
            return -1;
        }
    }
}
